/**
 * An agent's Steps tab: its steps and hand-offs as a graph (laid out by steps.ts), and a
 * side panel that edits whichever step or hand-off is selected. A step's "+" adds the next
 * step with a hand-off to it. Edits go to the editor's draft, saved with the header's Save.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, { Controls, Handle, Position, type NodeProps, type ReactFlowInstance } from 'reactflow';
import 'reactflow/dist/style.css';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  CAPABILITIES,
  CONNECTORS,
  EVENTS,
  MODELS,
  PLIABLE,
  RUN_BY_HAND,
  runOnSchedule,
  fromList,
  toList,
  type Capability,
  type Connection,
  type Edge,
  type Member,
  type TeamDoc,
  type Trigger,
} from './catalog';
import {
  NODE_HEIGHT,
  NODE_WIDTH,
  addStep,
  handoffIndex,
  removeStep,
  setStep,
  stepIndex,
  stepNodeId,
  stepsGraph,
  type StepNodeData,
} from './steps';

type Selected = { kind: 'step' | 'handoff'; index: number } | null;

function StepNode({ data, selected }: NodeProps<StepNodeData & { onAdd: () => void }>) {
  return (
    <div
      style={{ width: NODE_WIDTH, height: NODE_HEIGHT }}
      className={cn('relative rounded-md border bg-card text-card-foreground px-3 py-2 shadow-sm', selected && 'ring-2 ring-primary')}
    >
      {/* Anchors for hand-off lines only: hand-offs are added with a step's "+", not drawn. */}
      <Handle id="in" type="target" position={Position.Top} className="opacity-0" />
      <div className="text-sm font-medium truncate">{data.name}</div>
      <div className="text-xs text-muted-foreground truncate">{data.model}</div>
      <div className="text-xs text-muted-foreground truncate" title={data.starts}>
        {data.starts}
      </div>
      <Handle id="out" type="source" position={Position.Bottom} className="opacity-0" />
      <Handle id="back-out" type="source" position={Position.Right} style={{ top: '65%' }} className="opacity-0" />
      <Handle id="back-in" type="target" position={Position.Right} style={{ top: '35%' }} className="opacity-0" />
      <button
        type="button"
        aria-label={`Add a step after ${data.name}`}
        title="Add a step after this one"
        className="nodrag nopan absolute left-1/2 -bottom-3 -translate-x-1/2 h-6 w-6 rounded-full border bg-card text-muted-foreground shadow-sm flex items-center justify-center hover:text-foreground hover:border-primary"
        onClick={(e) => {
          e.stopPropagation();
          data.onAdd();
        }}
      >
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

const nodeTypes = { step: StepNode };

export default function StepsGraph({ doc, setDoc, connections, hash }: {
  doc: TeamDoc;
  setDoc: (d: TeamDoc) => void;
  connections: Connection[];
  hash: string;
}) {
  const [selected, setSelected] = useState<Selected>(null);
  const [flow, setFlow] = useState<ReactFlowInstance | null>(null);
  const graph = useMemo(() => stepsGraph(doc), [doc]);
  const slack = connections.filter((c) => c.connector === 'slack');

  // A step added or removed can land outside the view; refit once React Flow has measured it.
  // Not on load, where fitView (or a step link) has already placed it.
  const stepCount = useRef(doc.members.length);
  useEffect(() => {
    if (stepCount.current === doc.members.length) return;
    stepCount.current = doc.members.length;
    const timer = setTimeout(() => flow?.fitView({ padding: 0.3, maxZoom: 1, duration: 200 }), 50);
    return () => clearTimeout(timer);
  }, [flow, doc.members.length]);

  // A step link from the tree (…/steps#step-<id>) selects and centres that step.
  useEffect(() => {
    const i = doc.members.findIndex((m) => m.id && `#step-${m.id}` === hash);
    const node = graph.nodes[i];
    if (!flow || !node) return;
    setSelected({ kind: 'step', index: i });
    flow.setCenter(node.position.x + NODE_WIDTH / 2, node.position.y + NODE_HEIGHT / 2, { zoom: 1, duration: 300 });
    // Only when the link or the loaded agent changes, not on every edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash, flow, doc.id]);

  const add = (after?: string) => {
    setDoc(addStep(doc, after));
    setSelected({ kind: 'step', index: doc.members.length });
  };
  const nodes = graph.nodes.map((n) => ({
    ...n,
    data: { ...n.data, onAdd: () => add(n.data.name) },
    selected: selected?.kind === 'step' && n.id === stepNodeId(selected.index),
  }));
  const edges = graph.edges.map((e) => ({
    ...e,
    selected: selected?.kind === 'handoff' && handoffIndex(e.id) === selected.index,
    style: { stroke: 'hsl(var(--muted-foreground))', strokeWidth: 1.5, strokeDasharray: e.data.gated ? '5 4' : undefined },
    labelStyle: { fill: 'hsl(var(--foreground))', fontSize: 11 },
    labelBgStyle: { fill: 'hsl(var(--card))' },
  }));

  const step = selected?.kind === 'step' ? doc.members[selected.index] : undefined;
  const handoff = selected?.kind === 'handoff' ? doc.edges[selected.index] : undefined;

  return (
    <div className="flex h-full min-h-[480px]">
      <div className="relative flex-1 min-w-0">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onInit={setFlow}
          onNodeClick={(_, n) => setSelected({ kind: 'step', index: stepIndex(n.id) })}
          onEdgeClick={(_, e) => setSelected({ kind: 'handoff', index: handoffIndex(e.id) })}
          nodesDraggable={false}
          nodesConnectable={false}
          deleteKeyCode={null}
          fitView
          fitViewOptions={{ padding: 0.3, maxZoom: 1 }}
          proOptions={{ hideAttribution: true }}
        >
          <Controls showInteractive={false} />
        </ReactFlow>
        <Button variant="outline" size="icon" className="absolute top-3 left-3" aria-label="Add step" title="Add a step" onClick={() => add()}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      <div className="w-[560px] flex-shrink-0 border-l overflow-auto p-4">
        {step ? (
          <StepCard
            m={step}
            index={selected!.index}
            connections={connections}
            onChange={(m) => setDoc(setStep(doc, selected!.index, m))}
            onRemove={
              doc.members.length > 1
                ? () => {
                    setDoc(removeStep(doc, selected!.index)!);
                    setSelected(null);
                  }
                : undefined
            }
          />
        ) : handoff ? (
          <div className="space-y-3">
            <h3 className="text-base font-semibold">Hand-off</h3>
            <p className="text-sm text-muted-foreground">
              When a step finishes, which step runs next. The step only fills in its output; this rule decides where the work goes and
              whether a person approves it first.
            </p>
            <HandoffRow
              e={handoff}
              steps={doc.members}
              slack={slack}
              onChange={(ne) => setDoc({ ...doc, edges: doc.edges.map((x, j) => (j === selected!.index ? ne : x)) })}
              onRemove={() => {
                setDoc({ ...doc, edges: doc.edges.filter((_, j) => j !== selected!.index) });
                setSelected(null);
              }}
            />
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Click a step or a hand-off to edit it. To add a step that takes over from another, use the + under that step.
          </p>
        )}
      </div>
    </div>
  );
}

const NONE = '__none__';
const BY_HAND = '__by_hand__';
const ON_SCHEDULE = '__on_schedule__';

/** A small "label + control" stack. */
export function F({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={`space-y-1 ${className ?? ''}`}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function Pick({ value, onChange, options, placeholder, className }: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  className?: string;
}) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger className={className ?? 'w-full'}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** path = value (or a comma list: any of them), as rows. */
function Clauses({ value, onChange, keyLabel, keyPlaceholder }: {
  value: Record<string, unknown>;
  onChange: (v: Record<string, unknown>) => void;
  keyLabel: string;
  keyPlaceholder: string;
}) {
  const rows = Object.entries(value);
  const set = (next: [string, unknown][]) => onChange(Object.fromEntries(next));
  return (
    <div className="space-y-2">
      {rows.map(([k, v], i) => (
        <div key={i} className="flex gap-2 items-end">
          <F label={keyLabel} className="flex-1">
            <Input value={k} placeholder={keyPlaceholder} onChange={(e) => set(rows.map((r, j) => (j === i ? [e.target.value, r[1]] : r)))} />
          </F>
          <F label="Is (comma list: any of)" className="flex-1">
            <Input
              value={fromList(v)}
              onChange={(e) => {
                const list = toList(e.target.value);
                set(rows.map((r, j) => (j === i ? [r[0], list.length > 1 ? list : (list[0] ?? '')] : r)));
              }}
            />
          </F>
          <Button variant="ghost" size="icon" aria-label="Remove condition" onClick={() => set(rows.filter((_, j) => j !== i))}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => set([...rows, ['', '']])}>
        <Plus className="h-3 w-3 mr-1" /> Add condition
      </Button>
    </div>
  );
}

function TriggerRow({ t, connections, onChange, onRemove }: {
  t: Trigger;
  connections: Connection[];
  onChange: (t: Trigger) => void;
  onRemove: () => void;
}) {
  const byHand = t.source === 'user';
  const scheduled = t.source === 'schedule';
  const fromEvents = !byHand && !scheduled;
  const conn = connections.find((c) => c.name === t.connection);
  const events = conn ? (EVENTS[conn.connector] ?? []) : [];
  const sources = [
    ...connections.filter((c) => EVENTS[c.connector]).map((c) => ({ value: c.name, label: `${c.name} (${CONNECTORS[c.connector]?.label ?? c.connector})` })),
    { value: ON_SCHEDULE, label: 'On a schedule' },
    { value: BY_HAND, label: 'Run by hand' },
  ];
  return (
    <div className="rounded border p-3 space-y-3">
      <div className="flex gap-2 items-end">
        <F label="From" className="flex-1">
          <Pick
            value={byHand ? BY_HAND : scheduled ? ON_SCHEDULE : (t.connection ?? '')}
            placeholder="Choose a connection"
            options={sources}
            onChange={(v) =>
              onChange(
                v === BY_HAND
                  ? { ...RUN_BY_HAND }
                  : v === ON_SCHEDULE
                    ? runOnSchedule()
                    : { source: 'connection', connection: v, event: EVENTS[connections.find((c) => c.name === v)!.connector]![0]!.event },
              )
            }
          />
        </F>
        {scheduled && (
          <>
            <F label="Cron (minute hour day month weekday)" className="flex-1">
              <Input value={t.cron ?? ''} placeholder="0 9 * * 1" className="font-mono" onChange={(e) => onChange({ ...t, cron: e.target.value })} />
            </F>
            <F label="Timezone" className="flex-1">
              <Input value={t.timezone ?? ''} placeholder="America/New_York" onChange={(e) => onChange({ ...t, timezone: e.target.value.trim() || null })} />
            </F>
          </>
        )}
        {fromEvents && (
          <F label="When" className="flex-1">
            <Pick value={t.event} options={events.map((e) => ({ value: e.event, label: e.label }))} onChange={(event) => onChange({ ...t, event })} />
          </F>
        )}
        <Button variant="ghost" size="icon" aria-label="Remove trigger" onClick={onRemove}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      {fromEvents && (
        <>
          <Clauses
            value={t.filter ?? {}}
            onChange={(filter) => onChange({ ...t, filter })}
            keyLabel="Only if the event's"
            keyPlaceholder={conn?.connector === 'slack' ? 'channel' : 'priority'}
          />
          <F label="Group repeats into the same work by (event field, optional)">
            <Input
              value={t.work_item_key ?? ''}
              placeholder={conn?.connector === 'datadog' ? 'alert_id' : ''}
              onChange={(e) => onChange({ ...t, work_item_key: e.target.value || null })}
            />
          </F>
        </>
      )}
    </div>
  );
}

function ToolRow({ c, connections, onChange, onRemove }: {
  c: Capability;
  connections: Connection[];
  onChange: (c: Capability) => void;
  onRemove: () => void;
}) {
  const connector = c.connection === PLIABLE ? PLIABLE : connections.find((x) => x.name === c.connection)?.connector;
  const specs = connector ? (CAPABILITIES[connector] ?? []) : [];
  const spec = specs.find((s) => s.capability === c.capability);
  const sources = [
    { value: PLIABLE, label: 'Pliable (built in)' },
    ...connections.filter((x) => CAPABILITIES[x.connector]).map((x) => ({ value: x.name, label: `${x.name} (${CONNECTORS[x.connector]?.label ?? x.connector})` })),
  ];
  return (
    <div className="rounded border p-3 space-y-3">
      <div className="flex gap-2 items-end">
        <F label="Connection" className="flex-1">
          <Pick
            value={c.connection}
            placeholder="Choose a connection"
            options={sources}
            onChange={(v) => {
              const kind = v === PLIABLE ? PLIABLE : connections.find((x) => x.name === v)?.connector;
              onChange({ connection: v, capability: (kind && CAPABILITIES[kind]?.[0]?.capability) || '', params: {} });
            }}
          />
        </F>
        <F label="Can" className="flex-1">
          <Pick
            value={c.capability}
            options={specs.map((s) => ({ value: s.capability, label: s.label }))}
            onChange={(capability) => onChange({ ...c, capability, params: {} })}
          />
        </F>
        <Button variant="ghost" size="icon" aria-label="Remove tool" onClick={onRemove}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      {spec && spec.params.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {spec.params.map((p) => (
            <F key={p.key} label={`${p.label}${p.required ? ' *' : ''}`}>
              <Input
                value={p.kind === 'list' ? fromList(c.params[p.key]) : String(c.params[p.key] ?? '')}
                placeholder={p.placeholder}
                onChange={(e) => {
                  const params = { ...c.params };
                  const value = p.kind === 'list' ? toList(e.target.value) : e.target.value.trim();
                  if ((Array.isArray(value) && value.length) || (!Array.isArray(value) && value)) params[p.key] = value;
                  else delete params[p.key];
                  onChange({ ...c, params });
                }}
              />
            </F>
          ))}
        </div>
      )}
    </div>
  );
}

function OutputFields({ value, onChange }: { value: Record<string, string[]>; onChange: (v: Record<string, string[]>) => void }) {
  const rows = Object.entries(value);
  const set = (next: [string, string[]][]) => onChange(Object.fromEntries(next));
  return (
    <div className="space-y-2">
      {rows.map(([k, allowed], i) => (
        <div key={i} className="flex gap-2 items-end">
          <F label="Field" className="flex-1">
            <Input value={k} placeholder="action" onChange={(e) => set(rows.map((r, j) => (j === i ? [e.target.value, r[1]] : r)))} />
          </F>
          <F label="Allowed values (blank: any text)" className="flex-[2]">
            <Input
              value={allowed.join(', ')}
              placeholder="comment, propose"
              onChange={(e) => set(rows.map((r, j) => (j === i ? [r[0], toList(e.target.value)] : r)))}
            />
          </F>
          <Button variant="ghost" size="icon" aria-label="Remove field" onClick={() => set(rows.filter((_, j) => j !== i))}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => set([...rows, ['', []]])}>
        <Plus className="h-3 w-3 mr-1" /> Add field
      </Button>
    </div>
  );
}

function StepCard({ m, index, connections, onChange, onRemove }: {
  m: Member;
  index: number;
  connections: Connection[];
  onChange: (m: Member) => void;
  onRemove?: () => void;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-3 space-y-0">
        <CardTitle className="text-base whitespace-nowrap">Step {index + 1}</CardTitle>
        <Input className="max-w-xs" value={m.name} aria-label="Step name" onChange={(e) => onChange({ ...m, name: e.target.value })} />
        {onRemove && (
          <Button variant="ghost" size="sm" className="ml-auto text-destructive" onClick={onRemove}>
            Remove step
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-2 gap-3 max-w-lg">
          <F label="Model">
            <Pick value={m.model ?? 'sonnet'} options={MODELS} onChange={(model) => onChange({ ...m, model })} />
          </F>
          <F label="Budget per run (USD)">
            <Input
              type="number"
              min={0}
              step={0.5}
              value={m.budget_usd ?? ''}
              onChange={(e) => onChange({ ...m, budget_usd: e.target.value === '' ? null : Number(e.target.value) })}
            />
          </F>
        </div>

        <F label="Instructions">
          <Textarea
            className="min-h-[200px]"
            value={m.prompt}
            placeholder="What this step does, step by step, and what it should never do."
            onChange={(e) => onChange({ ...m, prompt: e.target.value })}
          />
        </F>

        <section className="space-y-2">
          <h4 className="text-sm font-semibold">Starts when</h4>
          <p className="text-xs text-muted-foreground">Leave empty for a step that only starts after another step hands off to it.</p>
          {m.triggers.map((t, i) => (
            <TriggerRow
              key={i}
              t={t}
              connections={connections}
              onChange={(nt) => onChange({ ...m, triggers: m.triggers.map((x, j) => (j === i ? nt : x)) })}
              onRemove={() => onChange({ ...m, triggers: m.triggers.filter((_, j) => j !== i) })}
            />
          ))}
          <Button variant="outline" size="sm" onClick={() => onChange({ ...m, triggers: [...m.triggers, { ...RUN_BY_HAND }] })}>
            <Plus className="h-3 w-3 mr-1" /> Add trigger
          </Button>
        </section>

        <section className="space-y-2">
          <h4 className="text-sm font-semibold">Tools</h4>
          <p className="text-xs text-muted-foreground">Every step can also search and read the Knowledge Base, and keep its own notes there.</p>
          {m.capabilities.map((c, i) => (
            <ToolRow
              key={i}
              c={c}
              connections={connections}
              onChange={(nc) => onChange({ ...m, capabilities: m.capabilities.map((x, j) => (j === i ? nc : x)) })}
              onRemove={() => onChange({ ...m, capabilities: m.capabilities.filter((_, j) => j !== i) })}
            />
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() => onChange({ ...m, capabilities: [...m.capabilities, { connection: PLIABLE, capability: 'ellie.ask', params: {} }] })}
          >
            <Plus className="h-3 w-3 mr-1" /> Add tool
          </Button>
        </section>

        <section className="space-y-2">
          <h4 className="text-sm font-semibold">Output</h4>
          <p className="text-xs text-muted-foreground">
            Fields the step fills in when it finishes, besides its summary. Hand-offs choose the next step on these.
          </p>
          <OutputFields value={m.output_fields} onChange={(output_fields) => onChange({ ...m, output_fields })} />
        </section>
      </CardContent>
    </Card>
  );
}

function HandoffRow({ e, steps, slack, onChange, onRemove }: {
  e: Edge;
  steps: Member[];
  slack: Connection[];
  onChange: (e: Edge) => void;
  onRemove: () => void;
}) {
  const from = steps.find((s) => s.name === e.from);
  const [field, value] = Object.entries(e.when)[0] ?? ['', ''];
  const allowed = (from && field && from.output_fields[field]) || [];
  const stepOptions = steps.map((s) => ({ value: s.name, label: s.name }));
  return (
    <div className="rounded border p-3 space-y-3">
      <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end">
        <F label="When step">
          <Pick value={e.from} options={stepOptions} placeholder="Choose a step" onChange={(v) => onChange({ ...e, from: v, when: {} })} />
        </F>
        <F label="finishes with">
          <Pick
            value={field || NONE}
            options={[{ value: NONE, label: 'any output' }, ...Object.keys(from?.output_fields ?? {}).filter(Boolean).map((k) => ({ value: k, label: k }))]}
            onChange={(k) => onChange({ ...e, when: k === NONE ? {} : { [k]: from?.output_fields[k]?.[0] ?? '' } })}
          />
        </F>
        {field ? (
          <F label="equal to">
            {allowed.length ? (
              <Pick value={String(value)} options={allowed.map((a) => ({ value: a, label: a }))} onChange={(v) => onChange({ ...e, when: { [field]: v } })} />
            ) : (
              <Input value={String(value ?? '')} onChange={(ev) => onChange({ ...e, when: { [field]: ev.target.value } })} />
            )}
          </F>
        ) : (
          <div />
        )}
        <Button variant="ghost" size="icon" aria-label="Remove hand-off" onClick={onRemove}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end">
        <F label="First">
          <Pick
            value={!e.gate ? NONE : e.gate.connection != null ? 'slack' : 'app'}
            options={[
              { value: NONE, label: 'no approval' },
              { value: 'app', label: 'an Admin approves in Pliable' },
              { value: 'slack', label: 'an Admin approves in Pliable or Slack' },
            ]}
            onChange={(v) => onChange({ ...e, gate: v === NONE ? null : v === 'app' ? {} : { connection: slack[0]?.name ?? '', channel: '' } })}
          />
        </F>
        {e.gate?.connection != null ? (
          <>
            <F label="Slack bot">
              <Pick
                value={e.gate.connection}
                placeholder={slack.length ? 'Choose a bot' : 'Add a Slack connection first'}
                options={slack.map((c) => ({ value: c.name, label: c.name }))}
                onChange={(connection) => onChange({ ...e, gate: { ...e.gate!, connection } })}
              />
            </F>
            <F label="Channel ID">
              <Input value={e.gate.channel ?? ''} placeholder="C0123456789" onChange={(ev) => onChange({ ...e, gate: { ...e.gate!, channel: ev.target.value.trim() } })} />
            </F>
          </>
        ) : (
          <>
            <div />
            <div />
          </>
        )}
        <div className="w-9" />
      </div>
      <F label="Then hand to step" className="max-w-xs">
        <Pick value={e.to} options={stepOptions} placeholder="Choose a step" onChange={(to) => onChange({ ...e, to })} />
      </F>
    </div>
  );
}
